"use client";

import { useDeviceType } from "@/shared/context/DeviceContext";
import { useUrlFilters } from "@/shared/hooks/useUrlFilters";
import Flex from "../common/Flex";
import WithSticky from "../HOC/withSticky";
import SortFilter from "./components/sortFilter";
import type { FilterOptions, SortFilterItem } from "./filter.types";
import { FILTERS_TO_RESET } from "./filters.constants";
import FilterChipList from "./components/FilterChipList";

const MobileFilter = dynamic(
  () => import("@/shared/components/filters/mobileFilter"),
  {
    ssr: false,
  }
);

const DesktopFilter = dynamic(() => import("./DesktopFilter"), {
  ssr: false,
});

import { filtersComponent as defaultFiltersComponent } from "./components";
import dynamic from "next/dynamic";

type FiltersComponentRegistry = typeof defaultFiltersComponent;

interface FiltersProps {
  filters: {
    name: keyof FiltersComponentRegistry;
    label: string;
    placeholder?: string;
    id: string;
    value: Parameters<
      FiltersComponentRegistry[keyof FiltersComponentRegistry]
    >[0]["value"];
  }[];
  sortFilters?: SortFilterItem[];
  totalItems: number;
  options?: FilterOptions;
  components?: FiltersComponentRegistry;
}

/**
 * Renders a filter panel with accordion-style filter options.
 *
 * ## Extensibility Pattern
 * - By default, uses the built-in filter components registry (`filtersComponent`).
 * - Consumers can override the components by passing a `components` prop with the same type.
 * - This follows the Open/Closed Principle (SOLID): open for extension, closed for modification.
 *
 * ### Example (default components)
 * ```tsx
 * <Filters filters={filters} totalItems={100} options={{ sortOrder: true }} />
 * ```
 *
 * ### Example (custom components)
 * ```tsx
 * import { filtersComponent } from "@/shared/components/filters/components";
 * const customComponents = { ...filtersComponent, price: MyCustomPriceFilter };
 * <Filters filters={filters} totalItems={100} options={{ sortOrder: true }} components={customComponents} />
 * ```
 *
 * @param {FiltersProps} props - Component properties
 */

export default WithSticky(function Filters({
  filters,
  sortFilters,
  options = {
    sortOrder: true,
  },
  components,
}: FiltersProps) {
  const device = useDeviceType();
  // Use default registry unless overridden
  const filtersComponent = components ?? defaultFiltersComponent;

  const {
    isSubmittingFilter,
    resetFilters,
    changeFilter,
    hasActiveFilters,
    getCurrentFilterState,
    handleApplyFilters,
  } = useUrlFilters(FILTERS_TO_RESET);

  return (
    <Flex
      direction={"col"}
      gap={"2"}
      className="max-md:flex-col-reverse max-md:pb-2"
    >
      <FilterChipList />

      {options?.sortOrder && sortFilters && (
        <SortFilter filters={sortFilters} />
      )}

      {device === "mobile" ? (
        <MobileFilter
          filters={filters}
          handleChangeFilter={changeFilter}
          getCurrentFilterState={getCurrentFilterState}
          isFiltered={hasActiveFilters()}
          handleResetFilter={resetFilters}
          components={filtersComponent}
          handleApplyFilters={handleApplyFilters}
          isSubmittingFilter={isSubmittingFilter}
        />
      ) : (
        <DesktopFilter
          filters={filters}
          handleResetFilters={resetFilters}
          handleChangeFilter={changeFilter}
          hasActiveFilters={hasActiveFilters()}
          getCurrentFilterState={getCurrentFilterState}
          components={filtersComponent}
          handleApplyFilters={handleApplyFilters}
          isSubmittingFilter={isSubmittingFilter}
        />
      )}
    </Flex>
  );
});
